import { Component, Injectable, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { AcquisitionFilters, AcquisitionListComponent } from '../acquisition-list/acquisition-list.component';
import { ProjectsService } from '../../projects/projects.service';
import { getSessionBidsError } from '../session.utils';
import { getContainerProjectId } from '../../shared/container.utils';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Acquisition } from '../../shared/models/acquisition.model';
import { ContainerInfoValue } from '../../shared/models/container.model';
import { File } from '../../shared/models/file.model';
import { Project } from '../../shared/models/project.model';
import { Session, SessionBidsError } from '../../shared/models/session.model';
import { yieldClassProvider } from '../../shared/provider.utils';
import { SidebarService } from '../../sidebar.service';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FwIconModule, FwTooltipModule } from '@flywheel-io/vision';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';
import { BidsFilesComponent } from '../bids-files/bids-files.component';

export interface SessionActions {
  viewSessionTemplates: boolean
}

@Injectable()
export class SessionDataState {
  activeAcquisitionSort$ = new BehaviorSubject<Sort>({ active: 'created', direction: 'asc' })
  activeFileSort$ = new BehaviorSubject<Sort>({ active: 'created', direction: 'asc' })
  acquisitionFilters$ = new BehaviorSubject<AcquisitionFilters>({
    description: '',
    classification: '',
    created: null,
    fileType: '',
  })
  // Acquisitions that hold DICOM files
  dicomAcquisitions$ = new BehaviorSubject<Acquisition[]>([])
  // Files that have been modified or deleted
  fileChanges$ = new Subject<File>()
  // Files that are being dragged
  fileDragging$ = new Subject<File | null>()
  // Files that need to be moved to another acquisition in client state
  fileMove$ = new Subject<{ file: File, acquisitionId: string }[]>()
  // Files that need to be renamed
  fileRename$ = new Subject<string>() // file.file_id
  // Files that are shift-click selected
  fileSelection$ = new BehaviorSubject(new Set<string>()) // file.file_id
  originalTimezone$ = new BehaviorSubject(false)
  projectPermissions$ = new BehaviorSubject<Set<PermissionAction>>(new Set())
  runGearRulesOnMove$ = new BehaviorSubject(false)
}

@Component({
  host: { 'test-id': 'session-data' },
  selector: 'session-data',
  templateUrl: './session-data.component.html',
  styleUrls: ['./session-data.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwIconModule,
    FwTooltipModule,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
    BidsFilesComponent,
    AcquisitionListComponent,
  ],
  providers: [
    // If SessionDataState is not provided by a parent, provide our own
    yieldClassProvider(SessionDataState),
  ],
})
export class SessionDataComponent implements OnChanges, OnDestroy {
  @Input() collectionId?: string // passed to acquisition-list
  @Input() project: Project
  @Input() session: Session

  bidsView$ = this.sidebar.bidsViewActive$.asObservable()
  permissions: SessionActions = {
    viewSessionTemplates: false,
  }
  projectId$ = new Subject<string>()
  sessionError: SessionBidsError = null

  private subscriptions = {
    permissions: Subscription.EMPTY,
  }

  constructor(
    private projectsService: ProjectsService,
    private sidebar: SidebarService,
    private state: SessionDataState,
  ) {
    this.subscriptions.permissions = this.projectId$.pipe(
      distinctUntilChanged(),
      switchMap(projectId => this.projectsService.getProjectPermissions(projectId)),
      map(permissions => new Set(permissions))
    ).subscribe(permissions => {
      this.state.projectPermissions$.next(permissions);
      this.permissions = {
        viewSessionTemplates: permissions.has(PermissionAction.SessionTemplatesView),
      };
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.session && this.session?._id) {
      this.sessionError = getSessionBidsError(this.session);
      this.projectId$.next(getContainerProjectId(this.session));
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get projectBids(): ContainerInfoValue {
    return this.project?.info?.BIDS;
  }
}
